import { useEffect } from "react";
import { ShieldCheck, Wallet, HandCoins, ArrowLeftRight, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/format";
import { MemberCard } from "@/pages/Beranda";

const FEATURES = [
  { icon: Wallet, t: "Buku Tabungan Digital", d: "Pantau simpanan pokok, wajib & sukarela", tone: "from-sky-400 to-blue-600 shadow-blue-500/40" },
  { icon: HandCoins, t: "Pinjaman & Simulasi", d: "Ajukan pinjaman dan hitung angsuran", tone: "from-amber-400 to-orange-600 shadow-orange-500/40" },
  { icon: ArrowLeftRight, t: "Bayar & Transfer", d: "Diproses aman oleh tim admin CU", tone: "from-violet-500 to-purple-700 shadow-violet-500/40" },
];


const Mountains = ({ className }) => (
  <svg className={className} viewBox="0 0 400 80" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 80 L60 48 L95 62 L150 30 L190 52 L245 14 L300 50 L340 36 L400 60 V80 Z" fill="currentColor" />
    <path d="M238 20 L245 14 L254 22 L247 26 Z M144 35 L150 30 L157 37 Z" fill="white" />
  </svg>
);

const Hero = () => (
  <div className="relative overflow-hidden bg-gradient-to-br from-[#0b3fae] via-[#1452d6] to-[#0b2f8a] text-white px-6 pt-7 pb-20">
    <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 400 300" preserveAspectRatio="none" aria-hidden="true">
      <path d="M230 0 C300 40 360 50 400 30 V0 Z" fill="#2563eb" opacity="0.5" />
      <path d="M260 120 C320 80 370 50 400 35 V55 C360 75 320 100 280 130 Z" fill="#ef4444" />
      <path d="M250 135 C310 95 360 70 400 60 V68 C360 80 315 105 265 140 Z" fill="white" opacity="0.85" />
    </svg>
    <Mountains className="absolute right-0 bottom-14 w-56 h-14 text-white/30" />
    <svg className="absolute -bottom-px left-0 w-full h-16" viewBox="0 0 400 60" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 30 C60 10 110 12 160 22 C240 38 320 30 400 8 V60 H0 Z" fill="#eef4ff" />
      <path d="M0 38 C40 20 90 14 130 18 C80 20 40 34 0 58 Z" fill="#dc2626" />
    </svg>
    <div className="relative flex items-center gap-2 bg-white keep-white rounded-2xl pl-2 pr-3 py-2 w-fit shadow-lg shadow-blue-950/30 fade-up">
      <img src={LOGO_URL} alt="Logo CU Tuah Talino" className="h-14 w-14 object-contain" data-testid="login-logo" />
      <div className="w-px h-12 bg-slate-300" />
      <img src="/brand-wordmark.png" alt="Credit Union Tuah Talino - Koperasi Simpan Pinjam" className="h-12 w-auto object-contain" data-testid="login-wordmark" />
    </div>
    <div className="relative mt-6 fade-up" style={{ animationDelay: "80ms" }}>
      <p className="text-[22px] leading-tight">Selamat Datang di</p>
      <h1 className="font-display font-extrabold text-[30px] leading-tight tracking-tight" data-testid="login-title">CU TUAH TALINO</h1>
      <p className="text-[13px] text-blue-50 mt-1 max-w-[260px]">Simpanan, pinjaman, dan layanan digital anggota dalam satu aplikasi.</p>
    </div>
  </div>
);

const DEMO = { name: "Anggota CU", member_no: "CU0000003", account_no: "100000000032", joined_at: "2026-11-04T03:00:00+00:00", status: "aktif" };

const DemoCard = () => (
  <div className="fade-up" style={{ animationDelay: "260ms" }} data-testid="login-demo-card">
    <MemberCard user={DEMO} demo />
  </div>
);

const GoogleG = () => (
  <svg className="h-6 w-6" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export default function Login() {
  const { login } = useAuth();
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => { if (wasDark) root.classList.add("dark"); };
  }, []);
  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#eef4ff] relative overflow-hidden sm:shadow-2xl" data-testid="login-page">
      <Hero />
      <div className="relative px-5 -mt-8 pb-24 space-y-5">
        <div className="grid grid-cols-3 gap-2.5">
          {FEATURES.map(({ icon: Icon, t, d, tone }, i) => (
            <div key={t} className="bg-white/90 keep-white border border-white rounded-2xl px-2 pt-3 pb-3.5 text-center shadow-lg shadow-blue-900/10 fade-up" style={{ animationDelay: `${140 + i * 60}ms` }} data-testid={`login-feature-${i}`}>
              <div className={`mx-auto h-11 w-11 rounded-xl bg-gradient-to-br ${tone} text-white flex items-center justify-center shadow-md`}><Icon className="h-5 w-5" /></div>
              <div className="font-display font-extrabold text-[12.5px] leading-tight text-slate-900 mt-2">{t}</div>
              <div className="text-[10.5px] text-slate-600 leading-snug mt-1">{d}</div>
            </div>
          ))}
        </div>

        <DemoCard />

        <p className="flex items-center justify-center gap-2 text-[13px] text-slate-600 bg-blue-100/60 rounded-full py-2 px-5 w-fit mx-auto" data-testid="login-protected">
          <ShieldCheck className="h-5 w-5 text-blue-800" /> Data anggota terlindungi
        </p>

        <div className="space-y-3">
          <button onClick={login} data-testid="login-masuk-button"
            className="w-full flex items-center justify-center gap-3 bg-gradient-to-b from-blue-700 to-blue-900 hover:from-blue-800 active:scale-[0.98] text-white text-lg font-bold py-3.5 rounded-full shadow-lg shadow-blue-800/30 transition-[transform,background-color]">
            <LogIn className="h-6 w-6" /> Masuk
          </button>
          <button onClick={login} data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white keep-white hover:bg-slate-50 active:scale-[0.98] text-slate-900 text-base font-bold py-3.5 rounded-full border-2 border-blue-900 transition-[transform,background-color]">
            <GoogleG /> Masuk dengan Google
          </button>
        </div>
        <p className="text-center text-sm text-slate-600">
          Belum punya akun?{" "}
          <button onClick={login} data-testid="login-register-link" className="font-bold text-blue-800 underline underline-offset-2">Daftar / Hubungi Admin</button>
        </p>
      </div>
      <Mountains className="absolute bottom-0 left-0 w-full h-16 text-blue-200/60 pointer-events-none" />
    </div>
  );
}
